import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import MarkdownView from "@/components/MarkdownView";
import { toast } from "sonner";
import { Briefcase, MapPin, Clock, ArrowLeft, FileUp, Send, ChevronDown, ChevronUp } from "lucide-react";

function fileToBase64(f) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result).split(",")[1] || "");
    r.onerror = reject;
    r.readAsDataURL(f);
  });
}

export default function Careers() {
  const [vacancies, setVacancies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);     // inline collapsible "view more"
  const [selected, setSelected] = useState(null);          // full apply view
  const [form, setForm] = useState({ name: "", email: "", phone: "", cover_letter: "", cv: null });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get("/vacancies").then((r) => setVacancies(r.data)).finally(() => setLoading(false));
  }, []);

  const onCv = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 4 * 1024 * 1024) { toast.error("CV too large; max 4MB"); return; }
    setForm((s) => ({ ...s, cv: f }));
  };

  const apply = async (e) => {
    e.preventDefault();
    if (!form.cv) { toast.error("Please upload your CV"); return; }
    setSubmitting(true);
    try {
      const cv_base64 = await fileToBase64(form.cv);
      await api.post(`/vacancies/${selected.id}/apply`, {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        cover_letter: form.cover_letter.trim(),
        cv_filename: form.cv.name,
        cv_mime: form.cv.type || "application/pdf",
        cv_base64,
      });
      toast.success("Application submitted — check your inbox for a confirmation.");
      setSelected(null);
      setForm({ name: "", email: "", phone: "", cover_letter: "", cv: null });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not submit. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-kb-bg flex flex-col" data-testid="careers-page">
      <Navbar />
      <main className="flex-1 px-6 lg:px-12 py-16 max-w-5xl w-full mx-auto">
        {!selected ? (
          <>
            <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted">JOIN OUR TEAM</div>
            <h1 className="font-serif text-4xl lg:text-5xl text-kb-ink mt-2 leading-tight">
              Help us build <em className="italic text-kb-primary">Nepal&apos;s most rigorous</em> research lab.
            </h1>
            <p className="text-kb-ink-muted mt-4 max-w-2xl">
              We&apos;re a small, opinionated team obsessed with making research publication accessible. Below are the open roles — pick one that matches what you do best.
            </p>

            {loading ? (
              <div className="mt-10 kb-mono text-xs text-kb-ink-muted">Loading openings…</div>
            ) : vacancies.length === 0 ? (
              <div className="mt-10 text-center py-16 border border-dashed border-slate-300 rounded-sm bg-white">
                <Briefcase className="w-8 h-8 text-kb-ink-light mx-auto" strokeWidth={1.5} />
                <p className="mt-3 text-sm text-kb-ink-muted">No open positions right now — check back soon, or drop us a note at <a href="mailto:hello@khojbyte.com" className="text-kb-primary">hello@khojbyte.com</a>.</p>
              </div>
            ) : (
              <div className="mt-10 grid gap-4">
                {vacancies.map((v) => {
                  const expanded = expandedId === v.id;
                  return (
                    <div key={v.id} className="kb-card p-6" data-testid={`careers-vacancy-${v.id}`}>
                      <div className="flex flex-wrap items-start gap-5">
                        {v.poster_url && (
                          <img
                            src={v.poster_url}
                            alt=""
                            className="w-32 h-32 rounded-sm object-cover border border-slate-200 shrink-0"
                            data-testid={`careers-vacancy-${v.id}-poster`}
                          />
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted flex flex-wrap items-center gap-x-2 gap-y-1">
                            <span>{v.department || "KhojByte"}</span>
                            {v.location && <span>· <MapPin className="w-3 h-3 inline" /> {v.location}</span>}
                            {(v.employment_type || []).length > 0 && (
                              <span>· <Clock className="w-3 h-3 inline" /> {(v.employment_type || []).join(", ")}</span>
                            )}
                            {(v.work_modes || []).map((m) => (
                              <span key={m} className="px-1.5 py-0.5 rounded-sm border border-kb-primary/30 bg-kb-primary/5 text-kb-primary">{m}</span>
                            ))}
                          </div>
                          <h3 className="font-serif text-2xl text-kb-ink mt-2 leading-tight">{v.title}</h3>
                          {!expanded && (
                            <p className="text-sm text-kb-ink-muted mt-3 line-clamp-2 whitespace-pre-line">{v.description}</p>
                          )}
                        </div>
                      </div>

                      {expanded && (
                        <div className="mt-5 pt-5 border-t border-slate-200" data-testid={`careers-vacancy-${v.id}-details`}>
                          <MarkdownView source={v.description} />
                        </div>
                      )}

                      <div className="mt-5 pt-3 border-t border-slate-200 flex flex-wrap items-center justify-between gap-3">
                        <button
                          onClick={() => setExpandedId(expanded ? null : v.id)}
                          className="kb-mono text-[11px] uppercase tracking-wider text-kb-primary hover:text-kb-ink inline-flex items-center gap-1.5"
                          data-testid={`careers-vacancy-${v.id}-toggle-btn`}
                        >
                          {expanded ? <>View less <ChevronUp className="w-3.5 h-3.5" /></> : <>View more <ChevronDown className="w-3.5 h-3.5" /></>}
                        </button>
                        <button
                          onClick={() => setSelected(v)}
                          className="kb-btn-primary text-sm"
                          data-testid={`careers-apply-${v.id}-btn`}
                        >
                          Apply now
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        ) : (
          <>
            <button
              onClick={() => setSelected(null)}
              className="kb-mono text-xs text-kb-ink-muted hover:text-kb-primary inline-flex items-center gap-2"
              data-testid="careers-back-btn"
            >
              <ArrowLeft className="w-3.5 h-3.5" /> BACK TO OPENINGS
            </button>
            <div className="mt-4 grid lg:grid-cols-5 gap-8">
              <div className="lg:col-span-2">
                {selected.poster_url && (
                  <img src={selected.poster_url} alt="" className="w-full aspect-square object-cover rounded-sm border border-slate-200 mb-4" />
                )}
                <div className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted flex flex-wrap items-center gap-x-2 gap-y-1">
                  <span>{selected.department || "KhojByte"}</span>
                  {(selected.employment_type || []).length > 0 && <span>· {(selected.employment_type || []).join(", ")}</span>}
                  {(selected.work_modes || []).map((m) => (
                    <span key={m} className="px-1.5 py-0.5 rounded-sm border border-kb-primary/30 bg-kb-primary/5 text-kb-primary">{m}</span>
                  ))}
                </div>
                <h2 className="font-serif text-3xl text-kb-ink mt-2 leading-tight">{selected.title}</h2>
                {selected.location && <div className="kb-mono text-[11px] text-kb-ink-muted mt-2"><MapPin className="w-3 h-3 inline" /> {selected.location}</div>}
                <div className="mt-6">
                  <MarkdownView source={selected.description} />
                </div>
              </div>

              <form onSubmit={apply} className="lg:col-span-3 kb-card p-6 space-y-3" data-testid="careers-apply-form">
                <h3 className="font-serif text-xl text-kb-ink">Apply for this role</h3>
                <div className="grid sm:grid-cols-2 gap-3">
                  <div>
                    <label className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted">Full name</label>
                    <input required className="kb-input mt-1" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="careers-name-input" />
                  </div>
                  <div>
                    <label className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted">Email</label>
                    <input required type="email" className="kb-input mt-1" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="careers-email-input" />
                  </div>
                </div>
                <div>
                  <label className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted">Phone (optional)</label>
                  <input className="kb-input mt-1" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="careers-phone-input" />
                </div>
                <div>
                  <label className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted">Cover letter / why you?</label>
                  <textarea rows={5} className="kb-input mt-1 resize-none" placeholder="Briefly tell us why you'd be a great fit…"
                    value={form.cover_letter} onChange={(e) => setForm({ ...form, cover_letter: e.target.value })} data-testid="careers-cover-input" />
                </div>
                <div>
                  <label className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted">CV (PDF / DOCX · max 4MB)</label>
                  <label className="mt-1 flex items-center gap-2 border border-dashed border-slate-300 rounded-sm px-3 py-3 text-sm text-kb-ink-muted cursor-pointer hover:border-kb-primary">
                    <FileUp className="w-4 h-4" />
                    {form.cv ? <span className="text-kb-ink">{form.cv.name}</span> : <span>Click to upload your CV</span>}
                    <input type="file" className="hidden" accept=".pdf,.doc,.docx,application/pdf" onChange={onCv} required data-testid="careers-cv-input" />
                  </label>
                </div>
                <button type="submit" disabled={submitting} className="kb-btn-primary w-full justify-center" data-testid="careers-submit-btn">
                  <Send className="w-4 h-4" /> {submitting ? "Submitting…" : "Submit application"}
                </button>
                <p className="kb-mono text-[10px] text-kb-ink-light text-center">
                  By applying you agree to our <Link to="/privacy" className="text-kb-primary">privacy policy</Link>.
                </p>
              </form>
            </div>
          </>
        )}
      </main>
      <Footer />
    </div>
  );
}
